import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Button, Drawer } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import CalendlyPopup from "./Calendlypopup";
import AuthFormModal from "./AuthPanel/AuthFormModal";
import { useUser } from "../context";
import { TOKEN_KEY } from "./common";
import "../App.css";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [calendlyVisible, setCalendlyVisible] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Check if user is logged in (only check user context, not localStorage directly)
  const isLoggedIn = user?.token;

  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible);
  };

  const showCalendly = () => {
    setCalendlyVisible(true);
  };

  const hideCalendly = () => {
    setCalendlyVisible(false);
  };

  const showAuth = () => {
    setShowAuthModal(true);
  };

  const hideAuth = () => {
    setShowAuthModal(false);
  };

  // Determine if we're on recruiter or job seeker pages
  const isRecruiterPage = location.pathname === '/for-recruiters';
  const isJobSeekerPage = location.pathname === '/for-job-seekers';
  // Sign Up is the primary CTA on the two marketing pages; elsewhere Book Demo is.
  // Sign In, though, is shown on every public page: it used to appear only on
  // these two, so anyone landing on the home page had nowhere obvious to sign in.
  const showSignUp = isRecruiterPage || isJobSeekerPage;

  /**
   * @param {string} id
   */
  const scrollToSection = (id) => {
    // If we're not on the main page, navigate to main page first
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      setDrawerVisible(false);
      return;
    }
    
    // If we're on the main page, scroll to the section
    const element = document.getElementById(id);
    if (element) {
      setDrawerVisible(false);
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  
  return (
    <div className="navbar-container" style={isJobSeekerPage ? {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      background: 'transparent',
      zIndex: 100,
      borderBottom: 'none',
      boxShadow: 'none'
    } : {}}>
      <Link to="/" className="logo">
        <img src="/chronoflow.svg" alt="Logo" />
        <span className="logo-text">Chronoflow</span>
      </Link>
      <Menu
        mode="horizontal"
        defaultSelectedKeys={["1"]}
        className="menu"
        style={{ flex: 1, minWidth: 0, marginLeft: 40 }}
      >
        <Menu.Item key="1">
          <Link to="/for-recruiters">Recruiters</Link>
        </Menu.Item>
        <Menu.Item key="2">
          <Link to="/for-job-seekers">Job Seekers</Link>
        </Menu.Item>
        <Menu.Item key="3">
          <Link to="/job-board">Job Board</Link>
        </Menu.Item>
       
        <Menu.Item key="5" onClick={() => scrollToSection("pricing-cards")}>
          <span style={{ cursor: 'pointer' }}>Pricing</span>
        </Menu.Item>
        <Menu.Item key="6" onClick={() => scrollToSection("faq")}>
          <span style={{ cursor: 'pointer' }}>FAQs</span>
        </Menu.Item>
      </Menu>
      {isLoggedIn ? (
        <Button
          type="primary"
          onClick={() => navigate('/dashboard')}
          className="book-demo-button"
        >
          Dashboard
        </Button>
      ) : (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }} className="auth-buttons-desktop">
          <Button
            type="default"
            onClick={showAuth}
            style={{
              background: 'transparent',
              color: '#333',
              border: '1px solid #333',
              fontWeight: '500'
            }}
          >
            Sign In
          </Button>
          {showSignUp ? (
            <Button type="primary" onClick={showAuth} className="book-demo-button">
              Sign Up
            </Button>
          ) : (
            <Button type="primary" className="book-demo-button" onClick={showCalendly}>
              Book Demo
            </Button>
          )}
        </div>
      )}
      <CalendlyPopup isVisible={calendlyVisible} onClose={hideCalendly} />
      <MenuOutlined className="mobile-menu-icon" onClick={toggleDrawer} />
      <Drawer title="Menu" placement="right" visible={drawerVisible} onClose={toggleDrawer}>
        <Menu mode="vertical">
          <Menu.Item key="1">
            <Link to="/for-recruiters" onClick={toggleDrawer}>Recruiters</Link>
          </Menu.Item>
          <Menu.Item key="2">
            <Link to="/for-job-seekers" onClick={toggleDrawer}>Job Seekers</Link>
          </Menu.Item>
          <Menu.Item key="3">
            <Link to="/job-board" onClick={toggleDrawer}>Job Board</Link>
          </Menu.Item>
          <Menu.Item key="4" onClick={() => scrollToSection("about")}>
            <a href="#about">About</a>
          </Menu.Item>
          <Menu.Item key="5" onClick={() => scrollToSection("pricing-cards")}>
            <a href="#pricing-cards">Pricing</a>
          </Menu.Item>
          <Menu.Item key="6" onClick={() => scrollToSection("faq")}>
            <a href="#faq">FAQs</a>
          </Menu.Item>
          {isLoggedIn ? (
            <Menu.Item key="7">
              <Button
                type="primary"
                style={{ width: "100%" }}
                onClick={() => {
                  navigate('/dashboard');
                  toggleDrawer();
                }}
              >
                Dashboard
              </Button>
            </Menu.Item>
          ) : (
            <>
              <Menu.Item key="7">
                <Button
                  type="default"
                  style={{ 
                    width: "100%", 
                    marginBottom: "8px",
                    background: 'transparent',
                    color: '#001529',
                    border: '1px solid #001529'
                  }}
                  onClick={showAuth}
                >
                  Sign In
                </Button>
              </Menu.Item>
              <Menu.Item key="8">
                {showSignUp ? (
                  <Button type="primary" style={{ width: "100%" }} onClick={showAuth}>
                    Sign Up
                  </Button>
                ) : (
                  <Button type="primary" style={{ width: "100%" }} onClick={showCalendly}>
                    Book Demo
                  </Button>
                )}
              </Menu.Item>
            </>
          )}
        </Menu>
      </Drawer>
      <CalendlyPopup isVisible={calendlyVisible} onClose={hideCalendly} />
      {showAuthModal && (
        <AuthFormModal onCancel={hideAuth} />
      )}
    </div>
  );
};

export default Navbar;
